import type { InteractionResolver } from "@repo/app-types/content/structured/parser";
import type { OperationSegment } from "@repo/app-types/entities/configurationAgent";
import { parseConfigurationAgentMessage } from "@repo/app-types/helpers/configurationAgentParser";
import { messageItemStyle } from "@repo/ui/chat/messageItem";
import { ModelOutputContent } from "@repo/ui/structuredContent/modelOutputContent";
import type { UIMessage } from "ai";
import { Fragment, type FC, type ReactNode } from "react";

// Structured content styles its own elements, so these bubbles stay off the
// `prose` variant the old Markdown bubbles used (see `messageItemStyle`).
const assistantBubble = messageItemStyle({
  layout: "agent",
  role: "assistant",
}).bubble();

const userBubble = messageItemStyle({
  layout: "agent",
  role: "user",
}).bubble();

export interface ConfigurationAgentMessageContentProps {
  text: string;
  role: UIMessage["role"];
  /** True while this text part is still streaming in. */
  isStreaming: boolean;
  /**
   * Resolves the interaction ids cited in the agent's `<quote>`/`<customer>`
   * tags, typically from the tool results in the same thread. Build one with
   * `buildInteractionResolverFromMessages`.
   */
  resolveInteractionId: InteractionResolver;
  /**
   * Renders the card for an operation the agent proposed. Each editor supplies
   * its own card (playbook / learning journey / …) and its own apply
   * semantics; the message content only decides where the card sits relative to
   * the surrounding prose.
   */
  renderOperation: (segment: OperationSegment) => ReactNode;
}

/**
 * Renders a configuration-agent chat message.
 *
 * The agent interleaves prose with XML operation tags (`<add-section>`, …). We
 * split those apart with `parseConfigurationAgentMessage` *before* any
 * structured-content parsing, so operations render as interactive cards while
 * only the prose between them flows through the shared structured content
 * pipeline (`ModelOutputContent`) — the same rendering used for saved artifacts
 * and coaching notes. This keeps operations out of the structured content model
 * entirely: they never reach the parser, and the model needs no notion of them.
 *
 * User prompts go through the same pipeline: their input is about as trusted as
 * model output, so it's fine that the structured parser drops markup it doesn't
 * model (e.g. link destinations).
 */
export const ConfigurationAgentMessageContent: FC<
  ConfigurationAgentMessageContentProps
> = ({ text, role, isStreaming, resolveInteractionId, renderOperation }) => {
  if (role === "user") {
    return (
      <div className={userBubble}>
        <ModelOutputContent
          text={text}
          isStreaming={isStreaming}
          resolveInteractionId={resolveInteractionId}
        />
      </div>
    );
  }

  const segments = parseConfigurationAgentMessage(text);
  const hasOperations = segments.some(
    (segment) => segment.type === "operation",
  );

  // No operations: render the whole message as a single structured-content
  // bubble, skipping the per-segment split.
  if (!hasOperations) {
    return (
      <div className={assistantBubble}>
        <ModelOutputContent
          text={text}
          isStreaming={isStreaming}
          resolveInteractionId={resolveInteractionId}
        />
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-2">
      {segments.map((segment) =>
        segment.type === "text" ? (
          <div key={segment.id} className={assistantBubble}>
            <ModelOutputContent
              text={segment.text}
              isStreaming={isStreaming}
              resolveInteractionId={resolveInteractionId}
            />
          </div>
        ) : (
          <Fragment key={segment.id}>{renderOperation(segment)}</Fragment>
        ),
      )}
    </div>
  );
};
